'use client';

import { useRef, useState } from 'react';
import { z } from 'zod';

import { useAppForm } from '@/lib/form';
import { useGrantApplicationUpload } from '@/hooks/use-grant-application-upload';
import { PERSONAL_NUMBER_REGEX } from '@sapling/constants/grants';
import { useTRPC } from '@/lib/trpc/client';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@sapling/ui/components/ui/button';
import { FileUploadField } from '@/components/form-fields/file-upload-field';
import Link from 'next/link';

function SectionHeading({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div className="mb-6">
      <h2 className="text-xl font-semibold text-neutral-900">{title}</h2>
      {description && (
        <p className="mt-1 text-sm text-neutral-500">{description}</p>
      )}
    </div>
  );
}

function SuccessView({
  referenceId,
  scrollRef,
}: {
  referenceId: string;
  scrollRef: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <div ref={scrollRef} className="scroll-mt-20 text-center">
      <h2 className="mb-4 text-2xl font-semibold text-neutral-900">
        Tack för din ansökan!
      </h2>
      <p className="mb-2 text-lg text-neutral-600">
        Vi har tagit emot din ansökan och återkommer så snart vi kan.
      </p>
      <p className="mb-8 text-sm text-neutral-500">
        Ditt referensnummer: <strong>{referenceId}</strong>
      </p>
      <Button variant="outline" asChild>
        <Link href="/fond">Tillbaka till Fond</Link>
      </Button>
    </div>
  );
}

export function GrantApplicationForm() {
  const successRef = useRef<HTMLDivElement>(null);
  const [result, setResult] = useState<{ referenceId: string } | null>(null);
  const [submissionToken] = useState(() => crypto.randomUUID());
  const fileUpload = useGrantApplicationUpload();

  const trpc = useTRPC();
  const submitMutation = useMutation(
    trpc.grants.submitApplication.mutationOptions({
      onSuccess: (data) => {
        setResult(data);
        toast.success('Din ansökan har skickats!');
        setTimeout(() => {
          successRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
      },
      onError: (error) => {
        toast.error(`Ett fel inträffade: ${error.message}`);
      },
    }),
  );

  const form = useAppForm({
    defaultValues: {
      name: '',
      personalNumber: '',
      address: '',
      phone: '',
      email: '',
      aboutYourself: '',
      reasonForSupport: '',
      previousSupport: '',
      fundUsage: '',
      amount: '' as unknown as number,
      amountBasis: '',
      whyImportant: '',
      shortAndLongTerm: '',
      additionalInfo: '',
      howToVerify: '',
      willingToFollowUp: false,
      certifyCorrectInfo: false,
      consentToProcessing: false,
    },
    onSubmit: async ({ value }) => {
      await submitMutation.mutateAsync({
        ...value,
        personalNumber: value.personalNumber || undefined,
        amount: Number(value.amount) * 100,
        additionalInfo: value.additionalInfo || undefined,
        uploads: fileUpload.uploads,
        submissionToken,
      });
    },
  });

  if (result) {
    return (
      <SuccessView referenceId={result.referenceId} scrollRef={successRef} />
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
      className="space-y-12"
    >
      <form.AppForm>
        {/* 1. Personuppgifter */}
        <section>
          <SectionHeading title="Personuppgifter" />
          <div className="space-y-4">
            <form.AppField
              name="name"
              validators={{
                onBlur: z.string().min(1, 'Du måste ange ditt namn.'),
              }}
            >
              {(field) => (
                <field.TextField
                  label="Namn"
                  placeholder="Förnamn Efternamn"
                  autoComplete="name"
                />
              )}
            </form.AppField>

            <form.AppField
              name="personalNumber"
              validators={{
                onBlur: z
                  .string()
                  .refine(
                    (val) => val === '' || PERSONAL_NUMBER_REGEX.test(val),
                    'Personnummer måste anges i formatet ÅÅÅÅMMDD-XXXX.',
                  ),
              }}
            >
              {(field) => (
                <field.TextField
                  label="Personnummer"
                  placeholder="ÅÅÅÅMMDD-XXXX"
                  optional
                />
              )}
            </form.AppField>

            <form.AppField
              name="address"
              validators={{
                onBlur: z.string().min(1, 'Du måste ange din adress.'),
              }}
            >
              {(field) => (
                <field.TextField
                  label="Adress"
                  placeholder="Gatuadress, postnummer, ort"
                  autoComplete="street-address"
                />
              )}
            </form.AppField>

            <form.AppField
              name="phone"
              validators={{
                onBlur: z.string().min(1, 'Du måste ange ditt telefonnummer.'),
              }}
            >
              {(field) => (
                <field.TextField
                  label="Telefonnummer"
                  type="tel"
                  placeholder="070-123 45 67"
                  autoComplete="tel"
                />
              )}
            </form.AppField>

            <form.AppField
              name="email"
              validators={{
                onBlur: z
                  .string()
                  .email('Du måste ange en giltig e-postadress.'),
              }}
            >
              {(field) => (
                <field.TextField
                  label="E-postadress"
                  type="email"
                  placeholder="din.epost@exempel.se"
                  autoComplete="email"
                />
              )}
            </form.AppField>
          </div>
        </section>

        {/* 2. Om din situation */}
        <section>
          <SectionHeading title="Om din situation" />
          <div className="space-y-4">
            <form.AppField
              name="aboutYourself"
              validators={{
                onBlur: z
                  .string()
                  .min(10, 'Beskriv din situation med minst 10 tecken.'),
              }}
            >
              {(field) => (
                <field.TextareaField label="Berätta kort om dig själv och din nuvarande livssituation" />
              )}
            </form.AppField>

            <form.AppField
              name="reasonForSupport"
              validators={{
                onBlur: z
                  .string()
                  .min(
                    10,
                    'Förklara varför du söker stöd med minst 10 tecken.',
                  ),
              }}
            >
              {(field) => (
                <field.TextareaField label="Varför söker du stöd från Stiftelsen Sapling?" />
              )}
            </form.AppField>

            <form.AppField
              name="previousSupport"
              validators={{
                onBlur: z
                  .string()
                  .min(1, 'Du måste svara på om du sökt stöd tidigare.'),
              }}
            >
              {(field) => (
                <field.TextareaField
                  label="Har du sökt stöd från någon annan organisation tidigare (t.ex. kommun, kyrka, fond)?"
                  rows={3}
                />
              )}
            </form.AppField>
          </div>
        </section>

        {/* 3. Vad du söker stöd för */}
        <section>
          <SectionHeading
            title="Vad du söker stöd för"
            description="T.ex. utbildning, boendekostnad, barns aktivitet, medicin, rehabilitering, fritidsutrustning."
          />
          <div className="space-y-4">
            <form.AppField
              name="fundUsage"
              validators={{
                onBlur: z
                  .string()
                  .min(1, 'Beskriv vad fondmedlen ska gå till.'),
              }}
            >
              {(field) => (
                <field.TextareaField label="Beskriv vad fondmedlen ska gå till" />
              )}
            </form.AppField>

            <form.AppField
              name="amount"
              validators={{
                onBlur: z.coerce
                  .number()
                  .min(1, 'Beloppet måste vara minst 1 kr.')
                  .max(5000, 'Maxbeloppet är 5 000 kr per ansökan.'),
              }}
            >
              {(field) => (
                <field.NumberField
                  label="Hur stort belopp ansöker du om? (max 5 000 SEK)"
                  placeholder="Belopp i kronor"
                  min={1}
                  max={5000}
                />
              )}
            </form.AppField>

            <form.AppField
              name="amountBasis"
              validators={{
                onBlur: z.string().min(1, 'Ange hur du räknat ut beloppet.'),
              }}
            >
              {(field) => (
                <field.TextareaField
                  label="Ange hur du har räknat ut beloppet eller bifoga underlag (t.ex. kvitto, offert, faktura)"
                  rows={3}
                />
              )}
            </form.AppField>
          </div>
        </section>

        {/* 4. Motivering */}
        <section>
          <SectionHeading title="Motivering" />
          <div className="space-y-4">
            <form.AppField
              name="whyImportant"
              validators={{
                onBlur: z
                  .string()
                  .min(1, 'Förklara varför stödet är viktigt för dig.'),
              }}
            >
              {(field) => (
                <field.TextareaField label="Varför är stödet viktigt för dig just nu?" />
              )}
            </form.AppField>

            <form.AppField
              name="shortAndLongTerm"
              validators={{
                onBlur: z
                  .string()
                  .min(1, 'Beskriv hur stödet skulle hjälpa dig.'),
              }}
            >
              {(field) => (
                <field.TextareaField label="Hur skulle stödet från stiftelsen hjälpa dig eller din familj på kort och lång sikt?" />
              )}
            </form.AppField>

            <form.AppField name="additionalInfo">
              {(field) => (
                <field.TextareaField
                  label="Finns det något särskilt du vill att vi ska känna till innan vi beslutar?"
                  rows={3}
                  optional
                />
              )}
            </form.AppField>
          </div>
        </section>

        {/* 5. Uppföljning och ansvar */}
        <section>
          <SectionHeading
            title="Uppföljning och ansvar"
            description="T.ex. genom kvitto, bild, intyg eller kort återrapportering."
          />
          <div className="space-y-4">
            <form.AppField
              name="howToVerify"
              validators={{
                onBlur: z
                  .string()
                  .min(
                    1,
                    'Beskriv hur vi kan säkerställa att medlen används rätt.',
                  ),
              }}
            >
              {(field) => (
                <field.TextareaField
                  label="Hur kan vi säkerställa att medlen används till det du angivit?"
                  rows={3}
                />
              )}
            </form.AppField>

            <form.AppField name="willingToFollowUp">
              {(field) => (
                <field.CheckboxField label="Jag är beredd att lämna en kort återkoppling till stiftelsen efter att stödet använts, för att visa hur det hjälpte mig." />
              )}
            </form.AppField>
          </div>
        </section>

        {/* 6. Bifoga gärna */}
        <section>
          <SectionHeading
            title="Bifoga gärna"
            description="Eventuella kvitton, kostnadsförslag, intyg, rekommendationsbrev eller beskrivning/foto."
          />
          <FileUploadField
            files={fileUpload.files}
            totalSize={fileUpload.totalSize}
            uploading={fileUpload.uploading}
            error={fileUpload.error}
            onDrop={fileUpload.upload}
            onRemove={fileUpload.removeFile}
          />
        </section>

        {/* 7. Samtycke */}
        <section>
          <SectionHeading title="Samtycke" />
          <div className="space-y-4">
            <form.AppField
              name="certifyCorrectInfo"
              validators={{
                onBlur: z.boolean().refine((val) => val === true, {
                  message: 'Du måste intyga att uppgifterna är korrekta.',
                }),
              }}
            >
              {(field) => (
                <field.CheckboxField label="Jag intygar att de uppgifter jag lämnat är korrekta och att eventuellt beviljat stöd kommer att användas enligt denna ansökan. *" />
              )}
            </form.AppField>

            <form.AppField
              name="consentToProcessing"
              validators={{
                onBlur: z.boolean().refine((val) => val === true, {
                  message:
                    'Du måste samtycka till behandling av dina personuppgifter.',
                }),
              }}
            >
              {(field) => (
                <field.CheckboxField
                  label="Jag samtycker till att Stiftelsen Sapling behandlar mina personuppgifter (inklusive eventuellt personnummer och bifogade dokument) i enlighet med GDPR, enbart i syfte att handlägga denna ansökan. *"
                  description={
                    <>
                      Läs mer i vår{' '}
                      <Link
                        href="/sekretess-och-transparens"
                        className="text-neutral-900 underline"
                        target="_blank"
                      >
                        sekretesspolicy
                      </Link>
                      .
                    </>
                  }
                />
              )}
            </form.AppField>
          </div>
        </section>

        <form.SubmitButton
          label="Skicka ansökan"
          loadingLabel="Skickar ansökan..."
          disabled={submitMutation.isPending || fileUpload.uploading}
        />
      </form.AppForm>
    </form>
  );
}
